import {
    getDriversSourceConfig,
    normalizeDriversSource,
} from 'utils/driversOfGrowthConfig';
import {
    getTsBrandHashtags as getTikTokTsBrandHashtags,
    getTsBrandPosts as getTikTokTsBrandPosts,
    getTsCategoryHashtags as getTikTokTsCategoryHashtags,
    getTsCategoryPosts as getTikTokTsCategoryPosts,
} from '../api/tiktok.api';
import { shapeTranslations } from 'utils/entities';
import { tooltips } from '@/copy';
import { useMemo } from 'react';
import { useQuery } from 'react-query';

const toHashtagName = (item) =>
    item?.hashtag_name ||
    item?.keyword_name ||
    item?.hashtag ||
    item?.keyword ||
    null;

const toHashtagId = (item) =>
    item?.hashtag || item?.keyword || item?.id || toHashtagName(item);

const sortByVolume = (isNegativeWeek) => (a, b) =>
    isNegativeWeek
        ? (a.volume ?? 0) - (b.volume ?? 0)
        : (b.volume ?? 0) - (a.volume ?? 0);

const sortByIncrease =
    (isNegativePeriod, changeDataKey = 'non_cum_increase_WoW') =>
    (a, b) =>
        isNegativePeriod
            ? (a[changeDataKey] ?? 0) - (b[changeDataKey] ?? 0)
            : (b[changeDataKey] ?? 0) - (a[changeDataKey] ?? 0);

/**
 * Hook to fetch time series hashtags for a category on a specific date
 */
export const useTsCategoryHashtags = ({
    country,
    category,
    date,
    enabled = true,
}) =>
    useQuery({
        queryKey: ['ts-category-hashtags', { country, category, date }],
        queryFn: ({ queryKey }) =>
            getTikTokTsCategoryHashtags({ data: queryKey[1] }),
        enabled: !!(country && category && date && enabled),
    });

/**
 * Hook to fetch time series hashtags for a brand on a specific date
 */
export const useTsBrandHashtags = ({ country, brand, date, enabled = true }) =>
    useQuery({
        queryKey: ['ts-brand-hashtags', { country, brand, date }],
        queryFn: ({ queryKey }) =>
            getTikTokTsBrandHashtags({ data: queryKey[1] }),
        enabled: !!(country && brand && date && enabled),
    });

/**
 * Hook to fetch time series posts for a category on a specific date
 */
export const useTsCategoryPosts = ({
    country,
    category,
    date,
    enabled = true,
}) =>
    useQuery({
        queryKey: ['ts-category-posts', { country, category, date }],
        queryFn: ({ queryKey }) =>
            getTikTokTsCategoryPosts({ data: queryKey[1] }),
        enabled: !!(country && category && date && enabled),
    });

/**
 * Hook to fetch time series posts for a brand on a specific date
 */
export const useTsBrandPosts = ({ country, brand, date, enabled = true }) =>
    useQuery({
        queryKey: ['ts-brand-posts', { country, brand, date }],
        queryFn: ({ queryKey }) => getTikTokTsBrandPosts({ data: queryKey[1] }),
        enabled: !!(country && brand && date && enabled),
    });

/**
 * Helper to create paid types message for posts
 */
const createPaidTypesMessage = (
    { is_ad, partner_hashtag },
    dataSource = 'tiktok',
) => {
    const { paidTypesTooltipKeys } = getDriversSourceConfig(dataSource);

    if (!paidTypesTooltipKeys) return undefined;

    if (is_ad == 1 && partner_hashtag) {
        return tooltips[paidTypesTooltipKeys.paidAndPartner];
    }
    if (is_ad == 1) {
        return tooltips[paidTypesTooltipKeys.paidOnly];
    }
    if (partner_hashtag) {
        return tooltips[paidTypesTooltipKeys.partnerOnly];
    }
    return undefined;
};

/**
 * Combined hook to fetch both hashtags and posts for drivers of growth
 * Automatically selects category or brand endpoints based on provided params
 * @param {boolean} isNegativeWeek - Whether current week has negative WoW change (sorts ascending for negative, descending for positive)
 */
export const useDriversOfGrowth = ({
    country,
    category,
    brand,
    date,
    enabled = true,
    isNegativeWeek = false,
    dataSource = 'tiktok',
}) => {
    const isBrand = !!brand;
    const normalizedDataSource = normalizeDriversSource(dataSource);
    const sourceConfig = getDriversSourceConfig(normalizedDataSource);
    const {
        api: {
            getTsCategoryHashtags,
            getTsBrandHashtags,
            getTsCategoryPosts,
            getTsBrandPosts,
        },
        includeCategoryForBrandRequest,
        supportsPosts = true,
        changeDataKey = 'non_cum_increase_WoW',
    } = sourceConfig;

    const queryParams = {
        country,
        ...(isBrand ? { brand } : { category }),
        ...(includeCategoryForBrandRequest && isBrand && category
            ? { category }
            : {}),
        date,
    };

    const hashtagsQuery = useQuery({
        queryKey: [
            isBrand
                ? `${normalizedDataSource}-ts-brand-hashtags`
                : `${normalizedDataSource}-ts-category-hashtags`,
            queryParams,
        ],
        queryFn: ({ queryKey }) =>
            isBrand
                ? getTsBrandHashtags({ data: queryKey[1] })
                : getTsCategoryHashtags({ data: queryKey[1] }),
        enabled: !!(country && (category || brand) && date && enabled),
    });

    const postsQuery = useQuery({
        queryKey: [
            isBrand
                ? `${normalizedDataSource}-ts-brand-posts`
                : `${normalizedDataSource}-ts-category-posts`,
            queryParams,
        ],
        queryFn: ({ queryKey }) => {
            if (!supportsPosts) return Promise.resolve({ data: [] });
            if (isBrand) return getTsBrandPosts({ data: queryKey[1] });
            return getTsCategoryPosts({ data: queryKey[1] });
        },
        enabled: !!(
            supportsPosts &&
            country &&
            (category || brand) &&
            date &&
            enabled
        ),
    });

    const hashtagsData = useMemo(() => {
        const rawData = hashtagsQuery.data?.data;
        if (!rawData) return null;

        return rawData
            .map((item) => ({
                kw: toHashtagName(item),
                id: toHashtagId(item),
                volume: item?.data?.[changeDataKey] ?? item?.[changeDataKey],
                translations: shapeTranslations(
                    item?.google_translations,
                    item?.human_translations,
                ),
                flagId: item?.id,
                fetching: false,
                fetchingData: false,
            }))
            .filter((item) => item.kw && item.volume != null)
            .sort(sortByVolume(isNegativeWeek));
    }, [hashtagsQuery.data, isNegativeWeek, changeDataKey]);

    const postsData = useMemo(() => {
        if (!supportsPosts) return [];

        const rawData = postsQuery.data?.data;
        if (!rawData) return null;

        return rawData
            .map((item) => {
                const data = item.data || {};
                const periodChange =
                    data.wow ??
                    data.non_cum_WoW ??
                    data.mom ??
                    data.non_cum_MoM ??
                    null;
                const periodIncrease =
                    data.increase_wow ??
                    data.non_cum_increase_WoW ??
                    data.increase_mom ??
                    data.non_cum_increase_MoM ??
                    null;
                return {
                    id: item.id,
                    url: item.url,
                    author: item.author,
                    brand: item.brand,
                    non_cum_increase_WoW: periodIncrease,
                    non_cum_increase_MoM:
                        data.increase_mom ??
                        data.non_cum_increase_MoM ??
                        periodIncrease,
                    last_week_views:
                        data.last_week_views ?? data.last_month_views,
                    last_month_views: data.last_month_views,
                    total_post_views: data.total_post_views,
                    last_week_likes:
                        data.last_week_likes ?? data.last_month_likes,
                    last_month_likes: data.last_month_likes,
                    total_post_likes: data.total_post_likes,
                    non_cum_WoW: periodChange,
                    non_cum_MoM: data.mom ?? data.non_cum_MoM ?? periodChange,
                    wow: periodChange,
                    mom: data.mom ?? data.non_cum_MoM ?? periodChange,
                    create_timestamp: data.create_timestamp,
                    sentiment_score: data.sentiment_score,
                    sentimentScoreInt: data.sentiment_score,
                    sentiment_summary: data.sentiment_summary,
                    isPaid: !!(data.is_ad == 1 || data.partner_hashtag),
                    isShop: data.is_ad == 2,
                    paidTypesMessage: createPaidTypesMessage(
                        data,
                        normalizedDataSource,
                    ),
                };
            })
            .sort(sortByIncrease(isNegativeWeek, changeDataKey));
    }, [
        postsQuery.data,
        isNegativeWeek,
        normalizedDataSource,
        supportsPosts,
        changeDataKey,
    ]);

    return {
        hashtagsQuery,
        postsQuery,
        hashtagsData,
        postsData,
        isLoading:
            hashtagsQuery.isLoading || (supportsPosts && postsQuery.isLoading),
        isError: hashtagsQuery.isError || (supportsPosts && postsQuery.isError),
        isFetching:
            hashtagsQuery.isFetching ||
            (supportsPosts && postsQuery.isFetching),
    };
};
